/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { AsyncResultStatus, getUrlSync } from '@msdyn365-commerce/core';
import { IGiftCardExtend } from '@msdyn365-commerce/global-state';
import { GetCheckoutCartInput } from '@msdyn365-commerce-modules/retail-actions';
import { updateAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';

import { CartLine } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { IModuleStateProps, withModuleState } from '@msdyn365-commerce-modules/checkout-utilities';
import { IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import get from 'lodash/get';
import { action, computed, reaction, when } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { IConfiguration, IDXC_CapturePaymentResponse, IDXC_CreateOrderResponse, IDXC_GetConfigurationResponse, IDXC_GetOrderResponse } from '../../data-actions/generated/DataServiceEntities.g';
import { AfterpayGetOrderDataActionInput, IAfterpayGetOrderDataActionAction } from './actions/afterpay-get-order-data-action';
import { getGiftCardBalanceCheck, GiftCardBalanceCheckInput} from '../../data-actions/gift-card/get-card-balance';
import { AfterpayCapturePaymentDataActionInput, IAfterpayCapturePaymentDataActionAction } from './actions/afterpay-capture-payment-data-action';
import emptyActiveCart from './actions/empty-active-cart';
import { isEmpty } from '@msdyn365-commerce/retail-proxy';

import { Cart, CommerceProperty, GiftCard, SalesOrder } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { ICittaCheckoutPaymentInstrumentAfterpayData } from './citta-checkout-payment-instrument-afterpay.data';
import { ICittaCheckoutPaymentInstrumentAfterpayProps } from './citta-checkout-payment-instrument-afterpay.props.autogenerated';
import { createAddressObject, createDiscountsObject, createOrderItemsObject, stringToBoolean } from './components/citta-payment.utils';
import placeOrderAction, { ICheckoutDetailsObject } from './not-actions/place-order.action';

import CittaPaymentAfterpayTabContent from './components/citta-payment-afterpay-tab-content';
import CittaTelemetry from '../../common/telemetry/citta-telemetry';
import { AfterpayGetConfigDataActionInput, IAfterpayGetConfigDataActionAction } from './actions/afterpay-get-config-data-action';

import { getExtensionPropsLength, maskGiftCard } from './utils';

import withVisibilityObserver, {
    IVisibilyObserverProps
} from './components/with-visibility-observer';

import { AfterpayCreateOrderDataActionInput, IAfterpayCreateOrderDataActionAction, IAfterpayCreateOrderInput } from './actions/afterpay-create-order-data-action';
import { paymentStore } from './payment.store';


export interface ICheckoutPaymentInstrumentModuleProps extends ICittaCheckoutPaymentInstrumentAfterpayProps<ICittaCheckoutPaymentInstrumentAfterpayData>, IModuleStateProps, IVisibilyObserverProps { }

export interface IAfterpayConfigObject {
    type: string | undefined;
    minimumAmount: {
        amount: number;
        currency: string;
    };
    maximumAmount: {
        amount: number;
        currency: string;
    };
}

/**
 * Payment methods enum
 */
export enum PaymentMethods {
    Afterpay = 'Afterpay',
}

interface ICheckoutPaymentInstrumentState {
    errorMessage?: string;
    isFetchingPaymentConnector?: boolean;
    paymentConnectorHeight?: number;
    isPaymentVerificationRequried?: boolean;
    paymentVerificationPostData?: string;
    isPaymentProcessing?: boolean;
    isOverlayModal?: boolean;

    isBusy: boolean;

    ccCvc: string;
    ccExpiryMonth: string;
    ccExpiryYear: number;
    ccName: string;
    ccNumber: string;
    ccSessionId: string;
    cardNameError: boolean;
    cardNumError: boolean;
    cardCvcError: boolean;
    cardExpiryError: boolean;

    afterpayAvailable: boolean;
    afterpayConfiguration: IAfterpayConfigObject | null;
    afterpayMin: number;
    afterpayMax: number;
    afterpayOrderToken: string;
    laybuyAvailable: boolean;

    isProcessingOrder: boolean;
}

export interface ICheckoutPaymentOverlayModal {
    modal: INodeProps;
}

export interface ICheckoutPaymentInstrumentViewProps extends ICittaCheckoutPaymentInstrumentAfterpayProps<{}>, ICheckoutPaymentInstrumentState {
    className?: string;
    checkoutPaymentInstrument: IModuleProps;
    waiting?: React.ReactNode;
    alert?: React.ReactNode;
    paymentInformation?: React.ReactNode;
    addPaymentForm?: React.ReactNode;
    overlayModal?: ICheckoutPaymentOverlayModal;
    isVisible?: boolean;
}

const AP_PAYMENT_ERROR_DEFAULT = 'Afterpay payment error, please try again.';
const AP_PAYMENT_FAILED_DEFAULT = 'Afterpay order failed or was cancelled, please try again.';
const AP_SERVICE_ERROR_DEFAULT = 'There was an error with the Afterpay service, please try again.';

/**
 *
 * CheckoutPaymentInstrument component.
 * @extends {React.Component<ICittaCheckoutPaymentInstrumentAfterpayProps<ICittaCheckoutPaymentInstrumentAfterpayData>>}
 */
// @ts-expect-error
@withModuleState
@observer
export class CheckoutPaymentInstrument extends React.Component<ICheckoutPaymentInstrumentModuleProps> {

    @computed get getLoyaltyAmount(): number {
        const checkoutState = this.props.data.checkout.result;
        return checkoutState && checkoutState.loyaltyAmount ? checkoutState.loyaltyAmount : 0;
    }

    @computed get getCustomerAccountAmount(): number {
        const checkoutState = this.props.data.checkout.result;
        return checkoutState && checkoutState.customerAccountAmount ? checkoutState.customerAccountAmount : 0;
    }

    @computed get getGiftCardTotalAmount(): number {
        const checkoutState = this.props.data.checkout.result;
        if (!checkoutState || !checkoutState.giftCardExtends) {
            return 0;
        }
        return checkoutState.giftCardExtends.reduce((count: number, giftCard: IGiftCardExtend) => {
            const balance: number = giftCard.Balance || 0;
            return count + balance;

        }, 0);
    }

    @computed get shouldPaidByCard(): boolean {
        // const {
        //     data: {
        //         checkout
        //     },
        //     config
        // } = this.props;
        // if (!checkout.result) {
        //     return false;
        // }

        // const checkoutResult = checkout.result;
        // const cart = checkoutResult.checkoutCart.cart;
        // if (!cart || !cart.CartLines || cart.CartLines.length === 0) {
        //     return false;
        // }

        // const { paymentTenderType, tokenizedPaymentCard } = checkoutResult;
        // const isPaidByOtherPaymentSource = config.paymenTenderType !== paymentTenderType && tokenizedPaymentCard;

        // // Use the card for payment after all other payment methods
        // const amountDue = (cart.TotalAmount || 0) - this.getGiftCardTotalAmount - this.getLoyaltyAmount - this.getCustomerAccountAmount;

        return true;
    }

    @computed get isCartContainsItemsForShipping(): boolean {
        const pickupDeliveryModeCode = get(this.props, 'context.request.channel.PickupDeliveryModeCode');
        const emailDeliveryModeCode = get(this.props, 'context.request.channel.EmailDeliveryModeCode');
        const { channelDeliveryOptionConfig, featureState } = this.props.data;
        const multiplePickupStoreSwitchName = 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature';
        const retailMultiplePickUpOptionEnabled = featureState?.result
            ?.find(feature => feature.Name === multiplePickupStoreSwitchName)?.IsEnabled;

        return retailMultiplePickUpOptionEnabled ? (get(this.props, 'data.checkout.result.checkoutCart.cart.CartLines') || []).some(
            (cartLine: CartLine) => (cartLine.DeliveryMode !== channelDeliveryOptionConfig.result?.PickupDeliveryModeCodes?.find(deliveryMode => deliveryMode === cartLine.DeliveryMode) && cartLine.DeliveryMode !== emailDeliveryModeCode)
        ) : (get(this.props, 'data.checkout.result.checkoutCart.cart.CartLines') || []).some(
            (cartLine: CartLine) => (cartLine.DeliveryMode !== pickupDeliveryModeCode && cartLine.DeliveryMode !== emailDeliveryModeCode)
        );
    }

    @computed get isCartHasSelectedDeliveryMethods(): boolean {
        return (get(this.props, 'data.checkout.result.checkoutCart.cart.CartLines') || []).every(
            (cartLine: CartLine) => !!cartLine.DeliveryMode
        );
    }

    @computed get asyncResultStatus(): AsyncResultStatus | undefined {
        const isLoading = Object.values(this.props.data).some(data => get(data, 'status') === 'LOADING');
        if (isLoading) {
            return 'LOADING';
        }

        const isSuccess = Object.values(this.props.data).every(data => get(data, 'status') === 'SUCCESS');
        if (isSuccess) {
            return 'SUCCESS';
        }

        const isFailed = Object.values(this.props.data).some(data => get(data, 'status') === 'FAILED');
        if (isFailed) {
            return 'FAILED';
        }
        return;
    }

    @computed get hasSelectedItem(): boolean {
        return !!get(this.props.data, 'checkoutState.result.tokenizedPaymentCard');
    }

    @computed get isPaymentVerificationRedirection(): boolean {
        const { requestFormData, query } = this.props.context.request;
        return !!((requestFormData && query && query.pv === '1'));
    }

    @computed get requestUrlOrigin(): string {
        const origin = get(window, 'location.origin');
        const requestUrl =
            typeof get(this.props, 'context.request.url.requestUrl') === 'string' ? new URL(get(this.props, 'context.request.url.requestUrl')) : get(this.props, 'context.request.url.requestUrl');
        return origin || requestUrl.origin;
    }
    /**
     * checks if the cart exists and is not empty
     */
    @computed get isEmptyCart(): boolean {
        if (this.isEditor) {
            // Editorial mode: Ignore empty cart
            return false;
        }
        const checkout = this.props.data.checkout.result;
        const cart = get(checkout, 'checkoutCart.cart');
        return this.checkoutStatus === 'SUCCESS' && !!cart && !isEmpty(cart) && (!cart.CartLines || cart.CartLines.length === 0);
    }

    /**
     * checks if this is a live environment or the authoring tool editor
     */
    @computed get isEditor(): boolean {
        return get(this.props, 'context.request.params.isEditor');
    }

    /**
     * computes Afterpay instalment amount
     */
    @computed get getAfterpayInstalment(): string {
        return (this.getAmountDue / 4).toFixed(2);
    }

    /**
     * returns true if there is still an amount to be paid
     */
    @computed get isAmountDue(): boolean {
        return this.getAmountDue > 0;
    }

    /**
     * Gets current channel currency
     */
    @computed get getCurrency(): string {
        const channelConfiguration = this.props.data.channelConfiguration.result;

        return channelConfiguration!.Currency || '';
    }

    /**
     * Returns status of checkout data action
     */
    @computed get checkoutStatus(): string {
        return get(this.props, 'data.checkout.status');
    }

    /**
     * Construct input object for AfterPay Create Order API
     * Builds object from checkout state/cart fields
     */
    @computed get getAfterpayOrderInput(): AfterpayCreateOrderDataActionInput | null {
        const data = this.props.data;

        const cart = data.checkout.result?.checkoutCart.cart;
        const checkoutState = data.checkout.result;

        if (!cart || !cart.CartLines || !cart.CartLines.length || !cart.ExtensionProperties) {
            this.props.telemetry.error('Missing cart data');
            return null;
        }

        if (!checkoutState) {
            this.props.telemetry.error('Missing checkout state');
            return null;
        }

        if (!this.isAmountDue) {
            this.props.telemetry.error('No amount due');
            return null;
        }

        const firstName = checkoutState.shippingAddress?.Name;//
        const lastName = checkoutState.shippingAddress?.Name; //cart.ExtensionProperties.find(prop => prop.Key === 'LASTNAME')?.Value?.StringValue;
        const phoneNumber = checkoutState.shippingAddress?.Phone; //cart.ExtensionProperties.find(prop => prop.Key === 'PHONENUMBER')?.Value?.StringValue;

        if (!(firstName && lastName)) {
            this.props.telemetry.error('missing contact details');
            return null;
        }

        // NOTE: this needs to match Afterpay account currency - NZD for NZ etc.
        const orderCurrency = this.getCurrency;

        // Address objects
        const billingObject = createAddressObject(checkoutState.billingAddress);
        const shippingObject = createAddressObject(checkoutState.shippingAddress);

        // Items array object
        const itemsObject = createOrderItemsObject(cart.CartLines, orderCurrency, this.props.data.products.result);
        const discountsObject = createDiscountsObject(cart.CartLines, checkoutState.giftCards, orderCurrency);
        const returnUrl = this.getCittaPaymentReturnUrl('AP');

        // Assemble createOrder input object
        const orderInput: IAfterpayCreateOrderInput = {
            totalAmount: { // REQUIRED
                amount: this.getAmountDue.toFixed(2), // REQUIRED
                currency: orderCurrency // REQUIRED
            },
            consumer: { // REQUIRED
                email: checkoutState.guestCheckoutEmail, // REQUIRED
                givenNames: firstName, // REQUIRED
                phoneNumber: phoneNumber,
                surname: lastName // REQUIRED
            },
            billing: billingObject,
            shipping: shippingObject,
            items: itemsObject,
            discounts: discountsObject,
            merchant: { // REQUIRED
                redirectCancelUrl: returnUrl, // REQUIRED
                redirectConfirmUrl: returnUrl // REQUIRED
            },
            merchantReference: cart.OrderNumber, // REQUIRED
            taxAmount: cart.TaxAmount ? {
                amount: cart.TaxAmount.toFixed(2),
                currency: orderCurrency
            } : undefined,
            shippingAmount: cart.ShippingChargeAmount ? {
                amount: cart.ShippingChargeAmount.toFixed(2),
                currency: orderCurrency
            } : undefined
        };

        this.logMessage(`Completed getAfterpayOrderInput - returnURL: ${returnUrl} amountDue: ${orderInput.totalAmount.amount} taxAmount: ${orderInput.taxAmount?.amount}
            routes: ${JSON.stringify(this.props.context.actionContext.requestContext.app.routes)}
            sitePath: ${this.props.context.actionContext.requestContext.sitePath}
            page url: ${window.location.href}
            location.pathname: ${window.location.pathname}
        `);
        return new AfterpayCreateOrderDataActionInput(orderInput);
    }


    public state: ICheckoutPaymentInstrumentState = {
        isFetchingPaymentConnector: true,
        ccNumber: '',
        ccName: '',
        ccExpiryMonth: '01',
        ccExpiryYear: new Date().getFullYear(),
        ccCvc: '',
        ccSessionId: '',
        cardNameError: false,
        cardNumError: false,
        cardCvcError: false,
        cardExpiryError: false,

        afterpayAvailable: true,
        afterpayConfiguration: null,
        afterpayMin: 0,
        afterpayMax: 0,
        afterpayOrderToken: '',

        laybuyAvailable: false,

        isBusy: false,
        isProcessingOrder: false
    };

    private hasRedirect: boolean = false;
    private ourGiftToYou: boolean = false;
    private giftWithPurchase:boolean = false;
    private afterpayOrderToken: string = '';

    private cittaTelemetry: CittaTelemetry = new CittaTelemetry(this.props.context.app.config.cittaInstrumentationKey);

    /**
     * Returns the amount left on the total (minus gift cards & loyalty amounts)
     * note we're not using loyalty, but it's here just in case
     */
    @computed get getAmountDue(): number {
        const cart = this.props.data.checkout?.result?.checkoutCart.cart;
        if (!cart || !cart.CartLines || !cart.CartLines.length) {
            return 0;
        }
        const giftCardAmount = this.computeGiftCardAmount();
        const loyaltyAmount = this.computedLoyaltyAmount();

        return (cart.TotalAmount || 0) - giftCardAmount - loyaltyAmount;
    }

    /**
     * Methods to calculate remaining Amount Due
     * @param actionInput
     */
    private computeGiftCardAmount = (): number => {
        const {
            data: { checkout }
        } = this.props;
        if (!checkout.result || !checkout.result.checkoutCart.cart) {
            return 0;
        }
        const giftCardCounter = (count: number, giftCard: any) => {
            return count + (giftCard.Balance || 0);
        };
        const giftCardTotalValue = checkout.result.giftCards.reduce(giftCardCounter, 0);
        const amount = (checkout.result.checkoutCart.cart.TotalAmount || 0) - this.computedLoyaltyAmount();
        return Math.min(giftCardTotalValue, amount);
    };

    private computedLoyaltyAmount = (): number => {
        const checkoutState = this.props.data.checkout.result;
        return (checkoutState && checkoutState.loyaltyAmount) || 0;
    }

    public componentDidMount(): void {
        when(
            () => this.asyncResultStatus !== 'LOADING',
            () => {
                this.init();
            }
        );
        reaction(
            () => this.asyncResultStatus !== 'FAILED' && this.shouldPaidByCard,
            () => {
                this.togglePayment();
            }
        );

        this.ourGiftToYou = this.props.config.ourGiftToYou || false;
        this.giftWithPurchase = this.props.config.giftWithPurchase || false;
    }

    public render(): JSX.Element | null {
        const ExtensionProperties = this.props.data.checkout?.result?.checkoutCart.cart?.ExtensionProperties;
        const _isAfterpayAvailable = stringToBoolean(ExtensionProperties?.find(prop => prop.Key === 'DXC_ISAFTERPAYAVAILABLE')?.Value!.StringValue!);

        const amountDue = this.getAmountDue;
        if (_isAfterpayAvailable && this.state.afterpayAvailable && amountDue) {
            return <>
                {this.renderAfterpayTab()}
            </>
        }
        return <></>
    }
    
    private renderAfterpayTab = (): React.ReactNode => {
        const imgSrc = 'https://files-ap-prod.cms.commerce.dynamics.com/cms/api/tstpxgfmq/binary/MFfBP';
        return (
            <>
                <CittaPaymentAfterpayTabContent
                    afterpayAvailable={this.state.afterpayAvailable}
                    isBusy={this.state.isBusy}
                    instalmentAmount={this.getAfterpayInstalment}
                    afterpayImgSrc={imgSrc}
                    processAfterpayOrder={this.processAfterpayOrder}
                />
            </>
        );
    }

    /**
     * Gets the return URL for the given payment method
     *
     * payment method ID string passed as Parameter (eg: 'CC', 'AP', 'LB')
     */
    private getCittaPaymentReturnUrl = (paymentMethod: string): string => {
        const baseUrl = window.location.origin;
        if (!baseUrl.length) {
            return '';
        }

        const checkoutPath = getUrlSync('checkout', this.props.context.actionContext) || '';
        const separator = checkoutPath.includes('?') ? '&' : '?';
        const url = decodeURI(window.location.search);
        const urlParams = new URLSearchParams(url);
        const cartId = urlParams.has('cartid') ? urlParams.get('cartid') : this.props.data.checkout.result?.checkoutCart?.cart?.Id;

        if (process.env.NODE_ENV !== 'production') {
            if (urlParams.has('mock')) {
                return `${baseUrl}/page?mock=${urlParams.get('mock')}&paymentMethod=${paymentMethod}&cartid=${cartId}`;
            }
        }

        return `${baseUrl}${checkoutPath}${separator}paymentMethod=${paymentMethod}&cartid=${cartId}`;
    }

    private createLogMessage = (message: string): string => {
        const { Id, OrderNumber, TotalItems } = this.props.data.checkout!.result!.checkoutCart.cart;
        const url: string = decodeURI(window.location.search);
        const urlParams = new URLSearchParams(url);
        const redirectCartId = urlParams.has('cartid') ? urlParams.get('cartid') : 'N/A';
        const origin = window.location.origin;

        const logMessage = `Message: - ${message} - Origin: ${origin} - CartId: ${Id} - RedirectCartId: ${redirectCartId} - ChannelRefId: ${OrderNumber || 'N/A'} - TotalItem: ${TotalItems || 'N/A'}`;

        return logMessage;
    }

    /**
     * Logs a message to the MSFT logging system
     */
    private logMessage = (message: string): void => {
        const logMessage = this.createLogMessage(message);
        this.cittaTelemetry.trace(logMessage);
        this.props.context.telemetry.error(logMessage);
    }

    /**
    * Logs a message to the MSFT logging system
    */
    private logError = (error: Error, message: string): void => {
        const logMessage = this.createLogMessage(message);
        this.cittaTelemetry.trace(logMessage);
        this.cittaTelemetry.exception(error, logMessage);
        this.props.context.telemetry.error(logMessage);
    }

    private readonly redirectToPaymentVerification = async (): Promise<void> => {
        const { isPaymentVerificationRequried, paymentVerificationPostData } = this.state;
        if (!isPaymentVerificationRequried || !paymentVerificationPostData) {
            return;
        }

        const checkoutState = this.props.data.checkout.result;
        if (checkoutState) {
            await checkoutState.updatePaymentTenderType({ newPaymentTenderType: this.props.config.paymenTenderType });
            await checkoutState.saveDataInStorage({});
        }

        const redirectInfo = JSON.parse(paymentVerificationPostData);
        const { url, data } = redirectInfo;
        const form = document.createElement('form');

        form.method = 'POST';
        form.action = url;

        for (const propertyName of Object.keys(data)) {
            const element = document.createElement('input');
            element.name = propertyName;
            element.value = data[propertyName];
            form.appendChild(element);
        }

        document.body.appendChild(form);
        form.submit();
    };

    /**
     * Calls afterpay GetConfig Api and returns result
     */
    private afterpayGetConfiguration = async (): Promise<IDXC_GetConfigurationResponse> => {
        const actionInput = new AfterpayGetConfigDataActionInput();
        return IAfterpayGetConfigDataActionAction(actionInput, this.props.context.actionContext).catch(error => {
            this.logError(error, 'IAfterpayGetConfigDataActionAction failed');
            throw error;
        });
    }


    /**
     * Checks if Afterpay is available for the current order total
     */
    private checkAfterpayAvailability = (): void => {
        if (!this.state.afterpayConfiguration) {
            if (this.state.afterpayAvailable) {
                this.setState({ afterpayAvailable: false });
            }
            return;
        }

        const total = this.getAmountDue;
        const min = this.state.afterpayMin;
        const max = this.state.afterpayMax;
        const isValidAmount = min < total && total <= max;

        if (isValidAmount) {
            if (!this.state.afterpayAvailable) {
                this.setState({ afterpayAvailable: true });
            }
        } else {
            if (this.state.afterpayAvailable) {
                this.setState({ afterpayAvailable: false });
            }
        }
    }

    /**
     * Saves Afterpay config to state and determines if Afterpay payment option is available
     */
    private processAfterpayConfig = (afterpayConfig: IDXC_GetConfigurationResponse): void => {
        // Set configuration value in state        
        if (afterpayConfig && afterpayConfig.success && afterpayConfig.configuration) {
            const configResult: IConfiguration = afterpayConfig.configuration[0];
            // Parse result object for state - convert amounts to numbers
            // undefined or null converts to 0 NZD
            const configObject: IAfterpayConfigObject = {
                type: configResult.type,
                minimumAmount: configResult.minimumAmount ? {
                    amount: configResult.minimumAmount.amount && configResult.minimumAmount.amount !== '' ?
                        +configResult.minimumAmount.amount : 0,
                    currency: this.getCurrency
                } : {
                    amount: 0,
                    currency: 'NZD'
                },
                maximumAmount: configResult.maximumAmount ? {
                    amount: configResult.maximumAmount.amount && configResult.maximumAmount.amount !== '' ?
                        +configResult.maximumAmount.amount : 0,
                    currency: this.getCurrency
                } : {
                    amount: 0,
                    currency: 'NZD'
                }
            };
            // store in state
            this.setState({
                afterpayConfiguration: configObject,
                afterpayMin: configObject.minimumAmount.amount,
                afterpayMax: configObject.maximumAmount.amount
            });
            this.checkAfterpayAvailability();
        } else {
            this.displayCheckoutError(this.props.config.apNotAvailable, PaymentMethods.Afterpay);
            this.setState({
                afterpayAvailable: false
            });
            this.props.telemetry.error('Afterpay getConfig error in setup');
        }
    }


    /**
     * Logs page URL on redirection
     */
    private redirectLog = (url: string) => {
        const urlParams: URLSearchParams = new URLSearchParams(url);
        urlParams.delete('token'); // laybuy
        urlParams.delete('orderToken'); // afterpay
        urlParams.delete('sessionid'); // payment express

        this.logMessage(`Starting ejected checkout module: ${window.location.pathname}?${urlParams.toString()}`);
    }
    
    private getGiftCard = async (giftCardNumber: string, giftCardPinNumber: string): Promise<GiftCard | undefined> => {
        return getGiftCardBalanceCheck(new GiftCardBalanceCheckInput(giftCardNumber, giftCardPinNumber), this.props.context.actionContext)
            .then(activeGiftCard => {
                if (activeGiftCard instanceof Error) {
                    throw activeGiftCard;
                }
                if (!activeGiftCard.balance || activeGiftCard.balance === 0) {
                    throw new Error('Gift card has no balance left');
                }
                const cittaGiftCard = activeGiftCard;
                // tslint:disable-next-line: no-unnecessary-local-variable
                const giftCard: GiftCard = {
                    Id: giftCardNumber,
                    Balance: cittaGiftCard && cittaGiftCard.balance,
                    BalanceCurrencyCode: cittaGiftCard && cittaGiftCard.currencyCode
                };
                return giftCard;
            })
            .catch(() => {
                throw new Error('Invalid gift card code entered');
            });
    };

    private async _loadCartData(extensionProps: CommerceProperty[]): Promise<void> {
        const data = this.props.data;

        if (data.checkout.result && extensionProps && extensionProps.length) {
            this.logMessage(`Starting _loadCartData ${getExtensionPropsLength(extensionProps)}`);
            const checkoutState = data.checkout.result;

            const GC = extensionProps.filter((item: CommerceProperty) => {
                const key: string = item.Key!;
                return key.includes('GiftCard');
            });

            const GCTotal: number = GC.length / 4;
            for (let i = 0; i < GCTotal; i++) {

                const giftCardId = extensionProps.find((prop) => prop.Key === `GiftCardId_${i}`)?.Value?.StringValue;
                const giftCardPin = extensionProps.find((prop) => prop.Key === `GiftCardPin_${i}`)?.Value?.StringValue;
                try {
                    this.logMessage(`Starting getGiftCard i: ${i} - Id: ${maskGiftCard(giftCardId!)}`);
                    const giftCard = await this.getGiftCard(giftCardId!, giftCardPin!);
                    this.logMessage(`Finished getGiftCard - Id: ${maskGiftCard(giftCard?.Id!)} Balance: ${giftCard?.Balance}`);
                    if (giftCard) {
                        const ExtensionProperties: CommerceProperty[] = [];
                        ExtensionProperties.push({
                            Key: 'GIFTCARDPIN',
                            Value: {
                                StringValue: giftCardPin
                            }
                        });

                        giftCard.ExtensionProperties = ExtensionProperties;
                        checkoutState.addGiftCard({ giftCard: giftCard });
                        this.logMessage(`checkoutState ${JSON.stringify(checkoutState.giftCards)}`);
                    } else {
                        this.logMessage(`giftCard object not found - Id: ${maskGiftCard(giftCardId!)}`);
                    }
                } catch (error) {
                    this.logMessage(`Error adding giftCard ${JSON.stringify(error)}`);
                }
            }
        } else {
            throw new Error('Cart object missing data');
        }
    }

    /**
    * Calls Afterpay GetOrder Api and returns result
    * @param token order token
    */
    private afterpayGetOrder = async (token: string): Promise<IDXC_GetOrderResponse> => {
        const actionInput = new AfterpayGetOrderDataActionInput(token);
        return IAfterpayGetOrderDataActionAction(actionInput, this.props.context.actionContext);
    }

    
    /**
     * Calls Afterpay CapturePayment Api and stores result if successful
     */
     private afterpayCapturePayment = async (): Promise<IDXC_CapturePaymentResponse> => {
        const actionInput = new AfterpayCapturePaymentDataActionInput(this.afterpayOrderToken);
        return IAfterpayCapturePaymentDataActionAction(actionInput, this.props.context.actionContext);
    }

    /**
     * Creates object with payment details to pass to place order function
     */
     private createCheckoutDetailsObject = (
        paymentMethod: string,
        orderId?: string,
        cardToken?: string,
        serviceAccountId?: string,
        uniqueCardId?: string,
        maskedCardNumber?: string,
        cardName?: string,
        cardDateExpiry?: string,
        TxnRef?: string,
        DpstxnRef?: string
    ): ICheckoutDetailsObject => {
        return {
            paymentMethod: paymentMethod,
            orderId: orderId,
            cardToken: cardToken,
            serviceAccountId: serviceAccountId,
            uniqueCardId: uniqueCardId,
            maskedCardNumber: maskedCardNumber,
            cardName: cardName,
            cardDateExpiry: cardDateExpiry,
            TxnRef: TxnRef,
            DpstxnRef: DpstxnRef
        };
    }

    /**
     * Redirects to order confirmation page
     */
    private redirectToOrderConfirmationPage = async (salesOrder: SalesOrder): Promise<void> => {
        const orderConfirmationUrl = getUrlSync('orderConfirmation', this.props.context.actionContext) || '';
        if (!orderConfirmationUrl) {
            this.logMessage('No order confirmation url');
            return;
        }
        const separator = orderConfirmationUrl.includes('?') ? '&' : '?';
        const url = `${orderConfirmationUrl}${separator}transactionId=${salesOrder.Id}&onum=${salesOrder.ChannelReferenceId}`;
        window.location.assign(url);
    };

    /**
     * Places order with AX backend
     */
    private checkout = async (checkoutDetailsObject: ICheckoutDetailsObject = {}): Promise<void> => {
        const { context } = this.props;
        this.logMessage('Checkout function called');

        // It is placing order
        if (this.state.isBusy) {
            this.logMessage('Checkout is already busy');
            return;
        }

        this.setState({
            isBusy: true,
            isProcessingOrder: true
        });

        // Proceed checkout
        const salesOrder: SalesOrder = await placeOrderAction(context.actionContext, checkoutDetailsObject, this.ourGiftToYou, this.giftWithPurchase).catch(error => {
            this.setState({
                isProcessingOrder: false,
                isBusy: false
            });
            this.logError(error, `Place Order action failed: ${JSON.stringify(error)}`);
            this.displayCheckoutError(`Place Order failed: ${error}`);
            throw error;
        });

        this.logMessage('PlaceOrder end');

        // Remove purchased items from the active cart
        await emptyActiveCart(context.actionContext).catch(error => {
            this.logError(error, 'emptyActiveCartAction failed');
        });

        // Redirect to the order confirmation page
        this.logMessage('Starting redirectToOrderConfirmationPage');
        await this.redirectToOrderConfirmationPage(salesOrder);
    };

    /**
     * Calls payment finalization APIs and then checkout function
     * @param paymentMethod string to identify payment method
     */
    // tslint:disable-next-line
    private processCheckout = async (paymentMethod: PaymentMethods, creditCardCheckoutDetails?: ICheckoutDetailsObject) => {
        // CHECK DATA/CALL CHECKOUT
        switch (paymentMethod) {
           
            case PaymentMethods.Afterpay: {
                try {
                    this.logMessage('Starting _afterpayCapturePayment');
                    const capturePaymentResponse = await this.afterpayCapturePayment();
                    this.logMessage(`_afterpayCapturePayment API call successfully
                        success: ${capturePaymentResponse.success}
                        created: ${capturePaymentResponse.payment?.created || 'N/A'}
                        merchantReference: ${capturePaymentResponse.payment?.merchantReference || 'N/A'}
                        status: ${capturePaymentResponse.payment?.status || 'N/A'}
                        totalAmount: ${capturePaymentResponse.payment?.totalAmount?.amount || 'N/A'}
                        currency: ${capturePaymentResponse.payment?.totalAmount?.currency || 'N/A'}
                        errorText: ${capturePaymentResponse.errorText || 'N/A'}
                    `);
                    if (capturePaymentResponse.success && capturePaymentResponse.payment && capturePaymentResponse.payment.status === 'APPROVED') {
                        const checkoutDetails = this.createCheckoutDetailsObject(
                            PaymentMethods.Afterpay,
                            capturePaymentResponse.payment.id,
                            capturePaymentResponse.cardToken,
                            capturePaymentResponse.serviceAccountId,
                            capturePaymentResponse.uniqueCardId
                        );
                        try {
                            this.logMessage(`Starting AP checkout`);
                            // Proceed to checkout
                            await this.checkout(checkoutDetails);
                        } catch (error) {
                            //@ts-ignore
                            this.logError(error, `Process checkout - AP error`);
                            this.displayCheckoutError(`error: ${error}`, PaymentMethods.Afterpay);
                        }
                    } else {
                        const message = this.props.config.apPaymentError || AP_PAYMENT_ERROR_DEFAULT;
                        this.logMessage(`Process checkout - AP error 2 - ${message}`);
                        this.displayCheckoutError(message, PaymentMethods.Afterpay);
                    }
                } catch (error) {
                    //@ts-ignore
                    this.logError(error, `_afterpayCapturePayment failed ${JSON.stringify(error)}`);
                }
                break;
            }
            default: {
                this.logMessage(`${paymentStore.paymentMethod} : invalid payment method`);
                this.setState({
                    isProcessingOrder: false
                });
            }
        }
    }
    /**
     * Checks afterpay order is valid and proceeds to place order
     */
    private checkAfterpayPayment = async (urlParams: URLSearchParams): Promise<void> => {
        paymentStore.paymentMethod = PaymentMethods.Afterpay;
        // check for afterpay success
        if (urlParams.get('paymentMethod') === 'AP' && urlParams.has('orderToken')) {
            const orderToken = urlParams.get('orderToken');
            const afterpayStatus = urlParams.get('status') || 'FAILURE';

            if (afterpayStatus === 'SUCCESS' && orderToken) {
                // appears successful - check with getOrder API
                this.logMessage(`Starting _afterpayGetOrder API call`);
                await this.afterpayGetOrder(orderToken).then(async (getOrderResponse) => {
                    this.logMessage(`afterpayGetOrder API called successfully
                        errorText: ${getOrderResponse.errorText || 'N/A'}
                        expires: ${getOrderResponse.order?.expires || 'N/A'}
                        merchantReference: ${getOrderResponse.order?.merchantReference || 'N/A'}
                        totalAmount: ${getOrderResponse.order?.totalAmount?.amount || 'N/A'}
                        success: ${getOrderResponse.success}
                    `);
                    if (getOrderResponse.success && getOrderResponse.order) {
                        this.afterpayOrderToken = orderToken;
                        this.logMessage('Starting _processCheckout');
                        await this.processCheckout(PaymentMethods.Afterpay);
                    } else {
                        // ERROR STATE
                        const message = this.props.config.apServiceError || AP_SERVICE_ERROR_DEFAULT;
                        this.logMessage(`afterpayGetOrder API results failed check - ${message}`);
                        this.displayCheckoutError(message, PaymentMethods.Afterpay);
                    }
                }).catch(error => {
                    const message = this.props.config.apPaymentFailed || AP_PAYMENT_FAILED_DEFAULT;
                    this.logError(error, `afterpayGetOrder API call failed: ${JSON.stringify(error)} - ${message}`);
                    this.displayCheckoutError(message, PaymentMethods.Afterpay);
                    throw error;
                });
            } else {
                const message = this.props.config.apPaymentFailed || AP_PAYMENT_FAILED_DEFAULT;
                this.logMessage(`Afterpay status parms not SUCCESS: ${afterpayStatus} - ${message}`);
                this.displayCheckoutError(message, PaymentMethods.Afterpay);
            }
        } else {
            this.logMessage('Afterpay missing url parameters');
            this.displayCheckoutError('Afterpay missing url parameters', PaymentMethods.Afterpay);
        }
    }

    /**
     * Redirects to cart page
     */
    private redirectOnError = () => {
        const editCartLink = getUrlSync('cart', this.props.context.actionContext) || '';
        if (window && editCartLink) {
            this.logMessage('Redirecting');
            // Redirect to cart page if cart is empty
            window.location.assign(editCartLink);
        } else {
            this.logMessage('Cart is empty, but no editCartLink found');
        }
    }

    /**
     * Processes URL paramaters to handle redirection from payment methods
     */
    private handleReturnActions = async (urlParams: URLSearchParams): Promise<void> => {
        this.setState({
            isProcessingOrder: true
        });
        paymentStore.paymentMethod = urlParams.get('paymentMethod')!;
        const { OrderNumber } = this.props.data?.checkout?.result!.checkoutCart.cart;
        this.logMessage(`Get order number from cart: ${OrderNumber}`);

        // const cartId = urlParams.get('cartid')!;

        // TODO: Rework this check - or remove cartId from Url?
        /**
         * This check originally existed to confirm that we had reconstructed the correct cart object on return from redirection.
         * It shouldn't be needed with SSK2.0, but I'll leave the code here as a marker if a similar check needs to be added in future.
         */
        // console.log(`${cartId} -- ${Id}`);
        // if (cartId !== Id) {
        //     this.logMessage(`cartId mismatch from url & object - cartId: ${cartId} cart.Id: ${Id}`);
        //     this.setState({
        //         isProcessingOrder: false
        //     });
        //     throw new Error('cartId mismatch from url & object');
        // }

        try {
            const cart = this.props.data.checkout?.result?.checkoutCart.cart;
            if (cart?.ExtensionProperties) {
                await this._loadCartData(cart?.ExtensionProperties!);
            }
        } catch (error) {
            this.logMessage(`_loadCartData failed: ${JSON.stringify(error)}`);
            this.redirectOnError();
            this.logMessage(`completed redirectOnError`);
        }

        // switch according to payment method
        switch (paymentStore.paymentMethod) {
            case 'AP': {
                await this.checkAfterpayPayment(urlParams);
                break;
            }
            default: {
                this.logMessage(`Invalid payment method: ${paymentStore.paymentMethod}`);
                this.setState({
                    isProcessingOrder: false
                });
            }
        }
    }

    /**
     * Parse url parameters for redirection handling
     */
    private parseUrlParameters = async (): Promise<void> => {

        //add some logging here
        const url: string = decodeURI(window.location.search);
        const urlParams: URLSearchParams = new URLSearchParams(url);
        this.hasRedirect = urlParams.has('paymentMethod') && urlParams.has('cartid');
        this.logMessage(`landed on the checkout page with the URL: ${url}`);
        when(
            () => {
                return this.checkoutStatus === 'SUCCESS' && this.hasRedirect;
            },
            async () => {
                this.redirectLog(url);
                try {
                    this.logMessage(`Starting _handleReturnActions ${process.env.npm_package_version}`);
                    await this.handleReturnActions(urlParams);
                } catch (error) {
                    //@ts-ignore
                    this.logError(error, `componentDidMount hasRedirect failed: ${JSON.stringify(error)}`);
                }
            }
        );

        // @ts-ignore: Compiler not reconizing condition check for function params
        when(
            () => {
                return this.isEmptyCart && !this.hasRedirect;
            },
            () => {
                this.redirectOnError();
            }
        );
    }


    /**
     * Initial setup method for module
     *
     * loads needed scripts, then processes url for successful order on redirection
     */
    private setupModule = async (): Promise<void> => {
        // check for gift cards in cart
        const cart = this.props.data.checkout?.result?.checkoutCart.cart;

        if (cart) {
            // Add afterpay modal script to page html
            const afterpayScript = document.createElement('script');
            afterpayScript.src = cart.ExtensionProperties!.find(prop => prop.Key === 'DXC_AFTERPAYSCRIPTURL')!.Value!.StringValue!;
            afterpayScript.async = true;
            document.body.appendChild(afterpayScript);
        }

        if (cart && cart.CartLines && cart.CartLines.length > 0) {
            const giftCard = cart.CartLines.find((item) => item.ItemId === 'GiftCard');
            if (!giftCard) {
                // only setup afterpay if no gift cards in cart
                // get Afterpay configuration
                const afterpayConfig: IDXC_GetConfigurationResponse = await this.afterpayGetConfiguration().catch(error => {
                    this.logError(error, 'afterpayGetConfiguration failed');
                    this.displayCheckoutError(this.props.config.apNotAvailable);
                    this.setState({
                        afterpayAvailable: false
                    });
                    throw error;
                });
                
                this.processAfterpayConfig(afterpayConfig);

                this.setState({
                    laybuyAvailable: true
                });
            } else  {
                this.setState({
                    afterpayAvailable: false
                });
            }
        }

        // parse url for order success
        // called after getConfig function to avoid potential conflicts
        await this.parseUrlParameters();
    }

    @action
    private readonly onSubmit = (): void => {
        const { isPrimaryPayment = true } = this.props.config;
        if (!isPrimaryPayment) {
            this.props.moduleState.onSkip();
        }
    };

    @action
    private readonly init = (): void => {
        this.props.moduleState.init({
            onContainerReady: this.onContainerReady,
            onSubmit: this.onSubmit
        });

        this.logMessage('Init modules - citta-checkout-payment-instrument-afterpay');
        this.setupModule().catch(error => {
            this.logError(error, 'payment module setup failed');
            throw error;
        });

        paymentStore.paymentMethod = PaymentMethods.Afterpay;    
    };

    private _addCartExtensionNumber(cart: Cart, key: string, value: number) {
        cart.ExtensionProperties = cart.ExtensionProperties || [];
        const property = cart.ExtensionProperties.find((prop) => prop.Key === key);
        if (property) {
            property.Value = { DecimalValue: value };
        } else {
            cart.ExtensionProperties.push({
                Key: key,
                Value: {
                    DecimalValue: value
                }
            });
        }
    }

    private _addCartExtension(cart: Cart, key: string, value: string) {
        cart.ExtensionProperties = cart.ExtensionProperties || [];
        const property = cart.ExtensionProperties.find((prop) => prop.Key === key);
        if (property) {
            property.Value = { StringValue: value };
        } else {
            cart.ExtensionProperties.push({
                Key: key,
                Value: {
                    StringValue: value
                }
            });
        }
    }

    /**
     * Adds gift card/s to extension props
     * @param cart cart
     */
    private _addGiftcardExtensionProperties(cart: Cart, giftcards: readonly GiftCard[]) {
        // TODO: add each gift card
        for (let i = 0; i < giftcards.length; i++) {
            const cardId = giftcards[i].Id;
            // @ts-ignore
            const cardPin = giftcards[i].Pin;

            const cardBalance = giftcards[i].Balance;
            const cardCurrency = giftcards[i].CardCurrencyCode;

            if (cardId && cardPin) {
                this._addCartExtension(cart, `GiftCardId_${i}`, cardId);
                this._addCartExtension(cart, `GiftCardPin_${i}`, cardPin);

                if (cardBalance) {
                    this._addCartExtensionNumber(cart, `GiftCardBalance_${i}`, cardBalance);
                }

                if (cardCurrency) {
                    this._addCartExtension(cart, `GiftCardCurrency_${i}`, cardCurrency);
                }
            }
        }
    }


    private async _saveCartExtensions(cart: Cart) {
        if (!cart) {
            this.logMessage(`saveCartExtensions error: No cart object given`);
            this.props.telemetry.error('No cart object given');
            throw new Error(`saveCartExtensions error: No cart object given`);
        }
        const cartExtensionProperties = cart.ExtensionProperties;

        await updateAsync({ callerContext: this.props.context.actionContext }, { Id: cart.Id, ExtensionProperties: cartExtensionProperties }).then(newCart => {
            if (newCart) {
                this.logMessage(`updateAsync cartId: ${newCart.Id} ${getExtensionPropsLength(newCart.ExtensionProperties!)}`);
                this.props.context.actionContext.update(new GetCheckoutCartInput(this.props.context.request.apiSettings), newCart);
            }
        }).catch(error => {
            this.logMessage(`saveCartExtensions error: ${error.statusCode} ${error.name} ${error.message} ${error.data.CorrelationId}`);
            throw new Error(`saveCartExtensions ${error}`);
        });
    }

    /**
     *
     * SAVE CHECKOUT DETAILS
     */
    private async _saveCheckoutDetails() {
        const cart = this.props.data.checkout.result!.checkoutCart.cart;
        const checkoutState = this.props.data.checkout.result;

        // gift card details
        if (cart && checkoutState) {
            if (checkoutState.giftCards.length > 0) {
                await this._addGiftcardExtensionProperties(cart, checkoutState.giftCards);
            }

            await this._saveCartExtensions(cart).catch(error => {
                // this.props.telemetry.error('saveCartExtensions failed');
                throw error;
            });

            const {
                customerAccountNumber,
                isAuthenticated,
                token
            } = this.props.context.request.user;

            this.logMessage(`ExtensionProperties _saveCheckoutDetails success - isAuthenticated: ${isAuthenticated}, hasToken: ${token ? 'true' : 'false'}, customerAccountNumber: ${customerAccountNumber}`);
        } else {
            this.props.telemetry.error('Error saving checkout details');
        }
    }

    /**
     * Enables and highlights the Payment message block and displays a message
     * @param message the message to display
     *
     * @param paymentMthd the payment message on which to display this error - null for all methods
     */
    private displayCheckoutError = (message: string, paymentMthd?: PaymentMethods) => {
        // showPaymentError(true, message, paymentMthd);
        this.setState({
            isProcessingOrder: false,
            isBusy: false
        });
    }


    /**
    * Calls Afterpay CreateOrder Api and returns result
    */
    private afterpayCreateOrder = async (): Promise<IDXC_CreateOrderResponse | void> => {
        const afterpayActionInput = this.getAfterpayOrderInput;

        // check if successful order input
        if (afterpayActionInput === null) {
            this.displayCheckoutError(this.props.config.completeAllFields, PaymentMethods.Afterpay);
            return this.setState({
                isBusy: false
            });
        }

        // store order input in state
        this.setState({ afterpayOrderDetails: afterpayActionInput });
        // call create order API
        const createOrderResult: IDXC_CreateOrderResponse = await IAfterpayCreateOrderDataActionAction(afterpayActionInput, this.props.context.actionContext).catch(error => {
            this.logError(error, 'IAfterpayCreateOrderDataActionAction failed');
            this.displayCheckoutError(this.props.config.apPaymentError, PaymentMethods.Afterpay);
            this.setState({
                isBusy: false,
                isProcessingOrder: false
            });
            throw error;
        });

        // check if error with create order
        if (!createOrderResult || !createOrderResult.success || !createOrderResult.token) {
            // error with create order
            this.displayCheckoutError(this.props.config.apServiceError, PaymentMethods.Afterpay);
            this.setState({
                isBusy: false
            });
            return;
        }

        // store token
        this.setState({ afterpayOrderToken: createOrderResult.token });

        return createOrderResult;
    }

    private processAfterpayOrder = async (): Promise<void> => {
        // Set module state to busy
        this.setState({ isBusy: true });

        this.logMessage('Start adding ExtensionProperties');
        // add the extension properties here?
        await this._saveCheckoutDetails().catch(error => {
            this.logMessage('saveCheckoutDetails failed');
            this.props.telemetry.error('There was an error saving your details, please try again later', PaymentMethods.Afterpay);
            this.setState({
                showSuccess: false,
                isBusy: false,
                isProcessingOrder: false
            });
            throw error;
        });

        const createOrderResult = await this.afterpayCreateOrder().catch(error => {
            this.logError(error, 'afterpayCreateOrder failed');
            this.displayCheckoutError(this.props.config.apPaymentError, PaymentMethods.Afterpay);
            this.setState({
                isBusy: false,
                isProcessingOrder: false
            });
            throw error;
        });

        // check if error with create order

        if (!createOrderResult || !createOrderResult.success || !createOrderResult.token) {
            this.logMessage('afterpayCreateOrder failure');
            this.displayCheckoutError(this.props.config.apPaymentError, PaymentMethods.Afterpay);
            this.setState({
                isBusy: false,
                isProcessingOrder: false
            });
            return;
        }

        // Initialise Afterpay script then redirect to portal
        // get region code
        const channelConfig = this.props.data.channelConfiguration.result;
        const regionIsoCode = channelConfig ? channelConfig.ChannelCountryRegionISOCode : 'NZ'; // default to NZ
        // @ts-ignore
        AfterPay.initialize({ countryCode: regionIsoCode });

        this.logMessage('redirect to afterpay portal');

        // @ts-ignore
        AfterPay.redirect({ token: createOrderResult.token });
    }

    @action
    private readonly togglePayment = () => {
        if (this.shouldPaidByCard && this.props.moduleState.isDisabled) {
            this.props.moduleState.onUpdating();
        } else if (!this.shouldPaidByCard && !this.props.moduleState.isDisabled) {
            this.props.moduleState.setHasError(false);
            this.props.moduleState.onDisable();
        }
    };

    @action
    private readonly onContainerReady = (): void => {
        this.redirectToPaymentVerification();
        this.props.moduleState.setIsSubmitContainer(false);
    };
}

export default withVisibilityObserver(CheckoutPaymentInstrument);
